import React from 'react';
import './Logo.css';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'full' | 'icon' | 'text';
  className?: string;
  animated?: boolean;
}

const sizes = {
  sm: { icon: 28, text: '1rem' },
  md: { icon: 40, text: '1.25rem' },
  lg: { icon: 56, text: '1.5rem' },
  xl: { icon: 72, text: '2rem' },
};

const Logo: React.FC<LogoProps> = ({
  size = 'md',
  variant = 'full',
  className = '',
  animated = true
}) => {
  const { icon: iconSize, text: textSize } = sizes[size];

  const Icon = () => (
    <svg
      width={iconSize}
      height={iconSize}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`logo-icon ${animated ? 'logo-animated' : ''}`}
    >
      <defs>
        {/* Storm gradient - blue to purple to cyan */}
        <linearGradient id="storm-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3B82F6" />
          <stop offset="50%" stopColor="#8B5CF6" />
          <stop offset="100%" stopColor="#06B6D4" />
        </linearGradient>

        {/* Lightning glow gradient */}
        <linearGradient id="lightning-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#06B6D4" />
          <stop offset="100%" stopColor="#3B82F6" />
        </linearGradient>

        {/* Outer glow filter */}
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>

        {/* Pulse glow filter */}
        <filter id="pulse-glow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="3" result="blur"/>
          <feMerge>
            <feMergeNode in="blur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      {/* Outer vortex ring - the storm's edge */}
      <circle
        cx="32"
        cy="32"
        r="28"
        stroke="url(#storm-gradient)"
        strokeWidth="2"
        fill="none"
        opacity="0.3"
        className="vortex-ring vortex-outer"
      />

      {/* Middle vortex ring */}
      <circle
        cx="32"
        cy="32"
        r="22"
        stroke="url(#storm-gradient)"
        strokeWidth="2"
        fill="none"
        opacity="0.5"
        className="vortex-ring vortex-middle"
      />

      {/* Inner vortex ring */}
      <circle
        cx="32"
        cy="32"
        r="16"
        stroke="url(#storm-gradient)"
        strokeWidth="2.5"
        fill="none"
        opacity="0.7"
        className="vortex-ring vortex-inner"
      />

      {/* Audio frequency bars - left side */}
      <g className="freq-bars freq-left" opacity="0.8">
        <rect x="8" y="28" width="3" height="8" rx="1.5" fill="#3B82F6" className="freq-bar" />
        <rect x="13" y="24" width="3" height="16" rx="1.5" fill="#6366F1" className="freq-bar" />
        <rect x="18" y="26" width="3" height="12" rx="1.5" fill="#8B5CF6" className="freq-bar" />
      </g>

      {/* Audio frequency bars - right side */}
      <g className="freq-bars freq-right" opacity="0.8">
        <rect x="43" y="26" width="3" height="12" rx="1.5" fill="#8B5CF6" className="freq-bar" />
        <rect x="48" y="24" width="3" height="16" rx="1.5" fill="#6366F1" className="freq-bar" />
        <rect x="53" y="28" width="3" height="8" rx="1.5" fill="#3B82F6" className="freq-bar" />
      </g>

      {/* Central lightning bolt - the heart of the tempest */}
      <g filter="url(#glow)" className="lightning-bolt">
        <path
          d="M36 12 L28 28 L34 28 L26 44 L30 44 L24 56 L40 36 L32 36 L42 18 L36 18 Z"
          fill="url(#lightning-gradient)"
          className="bolt-main"
        />
        {/* Lightning highlight */}
        <path
          d="M35 14 L29 27 L33 27 L28 40"
          stroke="white"
          strokeWidth="1"
          fill="none"
          opacity="0.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* Energy sparks around the bolt */}
      <g className="energy-sparks" opacity="0.7">
        <circle cx="20" cy="20" r="1.5" fill="#06B6D4" className="spark" />
        <circle cx="44" cy="20" r="1.5" fill="#06B6D4" className="spark" />
        <circle cx="18" cy="44" r="1.5" fill="#06B6D4" className="spark" />
        <circle cx="46" cy="44" r="1.5" fill="#06B6D4" className="spark" />
      </g>
    </svg>
  );

  const Text = () => (
    <span className="logo-text" style={{ fontSize: textSize }}>
      <span className="logo-text-tempest">Tempest</span>
      <span className="logo-text-radio">Radio</span>
    </span>
  );

  return (
    <div className={`logo-container ${className}`}>
      {(variant === 'full' || variant === 'icon') && <Icon />}
      {(variant === 'full' || variant === 'text') && <Text />}
    </div>
  );
};

export default Logo;
